// OSIS Cloud Settings - Amazon S3 Configuration
import React, { useState } from 'react';
import {
  Settings,
  Cloud,
  Key,
  Server,
  Shield,
  Globe,
  Database,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Save,
  TestTube,
  Info,
  Zap,
  HardDrive,
  Clock,
  Users,
  Bell,
  FileText
} from 'lucide-react';

const CloudSettings = () => {
  const [activeTab, setActiveTab] = useState('connection');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');

  const [s3Settings, setS3Settings] = useState({
    accessKeyId: 'AKIA***************',
    secretAccessKey: '********************************',
    bucketName: 'osis-storage-ro',
    region: 'eu-central-1',
    encryption: 'AES256',
    storageClass: 'STANDARD',
    multipartThreshold: '64MB',
    maxConnections: '10',
    timeout: '60',
    retries: '3'
  });

  const [securitySettings, setSecuritySettings] = useState({
    enableEncryption: true,
    encryptionType: 'AES256',
    enableVersioning: true,
    enableMFA: false,
    bucketLogging: true,
    publicReadWrite: false,
    lifecycle: true,
    corsEnabled: false
  });

  const [backupSettings, setBackupSettings] = useState({
    autoBackup: true,
    backupFrequency: 'daily',
    retentionPeriod: '90',
    compressFiles: true,
    incrementalBackup: true,
    backupTime: '02:00',
    maxBackupSize: '50GB',
    excludePatterns: '.tmp,.cache,*.log'
  });

  const [notificationSettings, setNotificationSettings] = useState({
    enableNotifications: true,
    emailOnSuccess: false,
    emailOnFailure: true,
    emailOnQuota: true,
    slackWebhook: '',
    notificationEmail: 'admin@osisgroup.ro'
  });

  const handleTestConnection = async () => {
    setTestingConnection(true);
    // Simulate connection test
    setTimeout(() => {
      setTestingConnection(false);
      setConnectionStatus('connected');
    }, 2000);
  };

  const handleSaveSettings = () => {
    // Save settings logic here
    console.log('Salvare setări...');
  };

  const tabs = [
    { id: 'connection', name: 'Conexiune S3', icon: Cloud },
    { id: 'security', name: 'Securitate', icon: Shield },
    { id: 'backup', name: 'Backup', icon: HardDrive },
    { id: 'notifications', name: 'Notificări', icon: Bell }
  ];

  const renderConnectionTab = () => (
    <div className="space-y-6">
      {/* Connection Status */}
      <div className={`p-4 rounded-lg border ${
        connectionStatus === 'connected' 
          ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
          : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
      }`}>
        <div className="flex items-center">
          {connectionStatus === 'connected' ? (
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
          )}
          <span className={`font-medium ${
            connectionStatus === 'connected' ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'
          }`}>
            {connectionStatus === 'connected' ? 'Conectat la Amazon S3' : 'Conexiune eșuată'}
          </span>
        </div>
      </div>

      {/* AWS Credentials */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Credențiale AWS
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Access Key ID
            </label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={s3Settings.accessKeyId}
                onChange={(e) => setS3Settings({...s3Settings, accessKeyId: e.target.value})}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder="AKIA..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Secret Access Key
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type={showSecretKey ? "text" : "password"}
                value={s3Settings.secretAccessKey}
                onChange={(e) => setS3Settings({...s3Settings, secretAccessKey: e.target.value})}
                className="w-full pl-10 pr-12 py-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder="Secret key..."
              />
              <button
                type="button"
                onClick={() => setShowSecretKey(!showSecretKey)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Test Connection */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleTestConnection}
          disabled={testingConnection}
          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors"
        >
          {testingConnection ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <TestTube className="w-4 h-4 mr-2" />
          )}
          {testingConnection ? 'Se testează...' : 'Testează Conexiunea'}
        </button>
        
        <button
          onClick={handleSaveSettings}
          className="flex items-center px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
        >
          <Save className="w-4 h-4 mr-2" />
          Salvează
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Setări Amazon S3
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Configurează conexiunea și setările pentru stocare cloud
              </p>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-cyan-600 border-b-2 border-cyan-600 bg-cyan-50 dark:bg-cyan-900/20'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {activeTab === 'connection' && renderConnectionTab()}
            {activeTab === 'security' && (
              <div className="text-center py-12">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Setări Securitate</h3>
                <p className="text-gray-600 dark:text-gray-400">Configurează criptarea și setările de securitate pentru S3</p>
              </div>
            )}
            {activeTab === 'backup' && (
              <div className="text-center py-12">
                <HardDrive className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Setări Backup</h3>
                <p className="text-gray-600 dark:text-gray-400">Configurează backup-ul automat și politicile de retenție</p>
              </div>
            )}
            {activeTab === 'notifications' && (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Notificări</h3>
                <p className="text-gray-600 dark:text-gray-400">Configurează notificările email și integrările</p>
              </div>
            )}
          </div>
        </div>

        {/* S3 Status Info */}
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-xl border border-orange-200 dark:border-orange-800 p-6">
          <div className="flex items-start">
            <div className="flex items-center justify-center w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-xl mr-4 flex-shrink-0">
              <Zap className="w-6 h-6 text-orange-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Informații Amazon S3 pentru OSIS
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div>
                  <p><span className="font-medium">Stocare maximă:</span> 100 GB</p>
                  <p><span className="font-medium">Regiune recomandată:</span> eu-central-1 (Frankfurt)</p>
                  <p><span className="font-medium">Criptare:</span> AES-256 server-side</p>
                </div>
                <div>
                  <p><span className="font-medium">Backup automat:</span> Zilnic la 02:00</p>
                  <p><span className="font-medium">Versioning:</span> Activat pentru toate fișierele</p>
                  <p><span className="font-medium">Lifecycle:</span> Migrare la Glacier după 90 zile</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudSettings;
